import type { useBookLessonContent } from "@/lib/hooks/bookLessons/useBookLessons";
import type { LessonDataSource } from "@ai-teach/utils";

export type LessonRows = ReturnType<typeof useBookLessonContent>["rows"];
export type LessonLines = ReturnType<typeof useBookLessonContent>["pageLines"];

// Only what the row is, never the example or note it carries: the author edits which words the
// run uses, and a word they keep is still taught with the sentence the book gives it.
function rowsOfType(rows: LessonRows, type: string): string[] {
  return rows
    .filter((row) => row.type === type)
    .sort((a, b) => a.position - b.position)
    .map((row) => row.text);
}

function linesOfKind(lines: LessonLines, kind: string): string[] {
  return lines.filter((line) => line.kind === kind).map((line) => line.text);
}

/**
 * What one part of the lesson prints, everything under it included — the source nests with " · ",
 * and the generator reads a part the same way.
 */
export function partItems(part: string, lines: LessonLines): string[] {
  return lines
    .filter((line) => line.section === part || line.section?.startsWith(`${part} · `))
    .map((line) => line.text);
}

/**
 * The grammar points behind a section's lines, the way the generator reads them: it keeps the whole
 * row a line names, tables and all, so the match is on the text. A line matching none was typed in,
 * and is the only grammar the generator has as bare text.
 */
export function grammarPointsFor(
  rows: LessonRows,
  lines: string[]
): { points: LessonRows; typed: string[] } {
  const points: LessonRows[number][] = [];
  const typed: string[] = [];

  for (const line of lines) {
    const text = line.trim();
    if (text === "") continue;
    const row = rows.find(
      (one) => one.type === "grammar" && one.text.trim().toLowerCase() === text.toLowerCase()
    );
    if (row) points.push(row);
    else typed.push(text);
  }
  return { points, typed };
}

/** One lesson's content as the author sees it per source: a plain list of lines, in book order. */
export function itemsFor(source: LessonDataSource, rows: LessonRows, lines: LessonLines): string[] {
  switch (source) {
    case "words":
      return rowsOfType(rows, "word");
    case "grammar":
      return rowsOfType(rows, "grammar");
    case "grammarExamples":
      return rowsOfType(rows, "grammarExamples");
    case "passages":
      return linesOfKind(lines, "passage");
    case "dialogues":
      return linesOfKind(lines, "dialogue");
  }
}
